import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toMessages } from "@/lib/form-errors"
import { cn } from "@/lib/utils"

/**
 * OKS-935 — the gift TTL control, shared by all three editors (UX §2).
 *
 * Purely presentational: it renders a label, a number input, the « jours »
 * suffix and an inline error. It owns NO submit behaviour, because the three
 * surfaces commit differently:
 *
 *   S1 variant row  — wrapped in its own Inertia `<Form method="patch">`,
 *                     committed on blur-when-changed
 *   S2 product row  — same
 *   S3 mission form — no Form of its own; it is a `name`d input inside the
 *                     parent mission `<Form>` and saves with the mission
 *
 * Keeping submit out of here is what lets one component serve both without a
 * mode flag.
 *
 * # ⚠️ Blank is the DEFAULT, not an error
 *
 * An empty field means "inherit the chain, and if nothing in the chain has a
 * value, never expire" (PRD OQ-1 / D5). The placeholder says « Toujours » so
 * the empty state reads as a deliberate setting rather than a missing one. Do
 * not add a `required` attribute or a client-side empty check.
 *
 * # Crew form rules
 *
 * Plain shadcn `Input` + `Label` with a `name` attribute. NO shadcn `Form` /
 * `FormField` / `FormMessage`, no react-hook-form, no zod. Errors come from the
 * Inertia render-prop bag, normalised through `toMessages` — values arrive as
 * `string | string[]` because `inertia_rails` flattens a single message to a
 * bare string, and indexing `[0]` would render one CHARACTER.
 *
 * # 375px
 *
 * `flex-wrap` + `basis-full` on the label at the narrow breakpoint puts the
 * label above the input on its own row (UX §2). `w-24` keeps the control from
 * eating the row on desktop.
 */
export interface GiftTtlFieldProps {
  /** Input `name` — the nested param path in S3, the flat field in S1/S2. */
  name: string
  /** Persisted value. `null` renders blank + the « Toujours » placeholder. */
  value: number | null
  /** Render-prop error bag entry for this field. */
  error?: string | string[]
  /** Overrides the default « TTL (jours) » label (S2 uses the product copy). */
  label?: string
  /** Optional helper line under the field (S3's inheritance hint). */
  hint?: string
  /** `id`, so the `<Label htmlFor>` association is real and not positional. */
  id: string
  testId?: string
  onCommit?: (event: React.FocusEvent<HTMLInputElement>) => void
  className?: string
}

export function GiftTtlField({
  name,
  value,
  error,
  label,
  hint,
  id,
  testId = "gift-ttl-field",
  onCommit,
  className,
}: GiftTtlFieldProps) {
  const { t } = useTranslation()

  return (
    <div
      className={cn("flex flex-wrap items-center gap-x-2 gap-y-1", className)}
      data-testid={testId}
    >
      <Label
        htmlFor={id}
        className="text-muted-foreground basis-full text-xs sm:basis-auto"
      >
        {label ?? t("settings.brand.products.ttl.label")}
      </Label>
      <Input
        id={id}
        name={name}
        type="number"
        min="1"
        step="1"
        // ⚠️ `?? ""` — an uncontrolled input given `null` warns and then
        // silently becomes controlled on first keystroke. The empty string is
        // the blank state the placeholder describes.
        defaultValue={value ?? ""}
        placeholder={t("settings.brand.products.ttl.forever")}
        className="w-24"
        onBlur={onCommit}
        data-testid={`${testId}-input`}
      />
      <span className="text-muted-foreground text-xs">
        {t("settings.brand.products.ttl.suffix")}
      </span>
      {hint ? (
        <p className="text-muted-foreground basis-full text-xs">{hint}</p>
      ) : null}
      <div className="basis-full">
        <InputError
          messages={toMessages(error)}
          className="text-xs"
          data-testid={`${testId}-error`}
        />
      </div>
    </div>
  )
}
